import React, { useEffect, useState } from 'react';
import axios from 'axios';

import Loading from '@components/Loading';

export interface LegalRouteParams {
  crmToken: string;
}

/** Update when the hosted privacy policy copy changes. */
export const PRIVACY_POLICY_LAST_REVISED = 'May 18, 2026';

/** Update when the hosted terms of service copy changes. */
export const TERMS_OF_USE_LAST_REVISED = 'May 18, 2026';

interface CrmTokenResponse {
  agencyId: number;
}

interface AgencyInfoResponse {
  name: string;
}

interface LegalDisplayNameState {
  displayName: string | null;
  isLoading: boolean;
  error: Error | null;
}

function buildApiUrl(path: string): string {
  const baseUrl = process.env.API_URL;
  if (!baseUrl) {
    throw new Error('API_URL is not configured');
  }

  return `${baseUrl.replace(/\/$/, '')}/${path}`;
}

async function fetchLegalAppDisplayName(crmToken: string, signal: AbortSignal): Promise<string> {
  const { data: crm } = await axios.get<CrmTokenResponse>(buildApiUrl(`crm/${encodeURIComponent(crmToken)}`), {
    signal,
  });

  if (!crm?.agencyId) {
    throw new Error('CRM token did not resolve to an agency');
  }

  const { data: agency } = await axios.get<AgencyInfoResponse>(buildApiUrl(`agency/${crm.agencyId}/info`), {
    signal,
  });

  if (!agency?.name) {
    throw new Error('Agency info did not include a name');
  }

  return agency.name;
}

export function useAgencyLegalDisplayName(crmToken: string): LegalDisplayNameState {
  const [state, setState] = useState<LegalDisplayNameState>({
    displayName: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!crmToken) {
      setState({
        displayName: null,
        isLoading: false,
        error: new Error('CRM token is required'),
      });

      return undefined;
    }

    const controller = new AbortController();

    setState({
      displayName: null,
      isLoading: true,
      error: null,
    });

    fetchLegalAppDisplayName(crmToken, controller.signal)
      .then((displayName) => {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          displayName,
          isLoading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (axios.isCancel(error) || controller.signal.aborted) {
          return;
        }

        console.error('[AgencyLegal] Failed to load agency display name', error);
        setState({
          displayName: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Failed to load agency display name'),
        });
      });

    return () => {
      controller.abort();
    };
  }, [crmToken]);

  return state;
}

export function AgencyLegalLoading() {
  return <Loading show />;
}

export function AgencyLegalError() {
  return (
    <AgencyLegalShell>
      <h1 style={agencyLegalStyles.title}>Legal information unavailable</h1>
      <p style={agencyLegalStyles.paragraph}>
        We could not load this agency&apos;s legal information. Please try again later or contact <SupportEmailLink />.
      </p>
    </AgencyLegalShell>
  );
}

export function AgencyLegalShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={agencyLegalStyles.page}>
      <header style={agencyLegalStyles.header}>
        <img alt="Comcate" src="/assets/logo_white.png" style={agencyLegalStyles.logo} />
      </header>

      <main style={agencyLegalStyles.main}>
        <article style={agencyLegalStyles.card}>{children}</article>
      </main>

      <footer style={agencyLegalStyles.footer}>
        <p style={agencyLegalStyles.footerText}>
          &copy; {new Date().getFullYear()} Comcate Software, Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export const SUPPORT_EMAIL = 'support@comcate.com';

export function SupportEmailLink() {
  const mailtoHref = `mailto:${SUPPORT_EMAIL}`;

  return <a href={mailtoHref}>{SUPPORT_EMAIL}</a>;
}

export function SectionWithId({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={agencyLegalStyles.section}>
      <h2 style={agencyLegalStyles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={agencyLegalStyles.section}>
      <h2 style={agencyLegalStyles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

export function LegalDocumentMeta({
  lastRevised,
  revisedLabel = 'Last Revised:',
}: {
  lastRevised: string;
  revisedLabel?: string;
}) {
  return (
    <div style={agencyLegalStyles.metaBar} aria-label="Document metadata">
      <span style={agencyLegalStyles.metaItem}>
        Powered by <strong style={agencyLegalStyles.metaStrong}>Comcate</strong>
      </span>
      <span style={agencyLegalStyles.metaItem}>
        {revisedLabel} <strong style={agencyLegalStyles.metaStrong}>{lastRevised}</strong>
      </span>
    </div>
  );
}

export const agencyLegalStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8fafc',
    fontFamily: 'Source Sans Pro, sans-serif',
    color: '#1e293b',
  },
  header: {
    backgroundColor: 'var(--primary, #1F4E96)',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    height: 36,
  },
  main: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 16px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    padding: '40px 48px',
    maxWidth: 800,
    width: '100%',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  title: {
    fontSize: 28,
    fontWeight: 600,
    color: '#0f172a',
    margin: '0 0 8px',
    lineHeight: 1.25,
  },
  meta: {
    fontSize: 14,
    color: '#64748b',
    margin: '0 0 6px',
  },
  metaBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: 14,
    rowGap: 8,
    margin: '4px 0 24px',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#64748b',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  metaStrong: {
    fontWeight: 600,
    color: '#0f172a',
  },
  metaMuted: {
    fontSize: 13,
    color: '#94a3b8',
    margin: '0 0 24px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e2e8f0',
    margin: '0 0 32px',
  },
  toc: {
    marginBottom: 36,
    padding: '20px 24px',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
  tocList: {
    margin: '12px 0 0',
    paddingLeft: 20,
    lineHeight: 1.7,
    fontSize: 15,
    color: '#334155',
  },
  tocListUnnumbered: {
    margin: '12px 0 0',
    paddingLeft: 0,
    listStyleType: 'none',
    lineHeight: 1.7,
    fontSize: 15,
    color: '#334155',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1e293b',
    margin: '0 0 12px',
  },
  subheading: {
    fontSize: 16,
    fontWeight: 600,
    color: '#334155',
    margin: '20px 0 10px',
  },
  paragraph: {
    margin: '0 0 12px',
    fontSize: 15,
    lineHeight: 1.65,
    color: '#334155',
  },
  list: {
    margin: '0 0 12px',
    paddingLeft: 22,
    fontSize: 15,
    lineHeight: 1.65,
    color: '#334155',
  },
  footer: {
    backgroundColor: '#f1f5f9',
    borderTop: '1px solid #e2e8f0',
    padding: '20px 32px',
    textAlign: 'center',
  },
  footerText: {
    fontSize: 13,
    color: '#64748b',
    margin: 0,
  },
};
